import { inject, provide, ref, computed, onBeforeUnmount } from '@vue/composition-api';
import * as signalR from '@microsoft/signalr';

export const SignalRKey = Symbol('SignalR');

const getNotificationApiBaseUrl = () => {
  if (typeof window === 'undefined') return 'https://app-indsiders-mynotification-api-dev.azurewebsites.net';
  const origin = window.location.origin;
  const isProd = origin.includes('indsiders.com') && !origin.includes('dev') && !origin.includes('homolog');
  const isHomolog = origin.includes('homolog');
  if (isProd) return 'https://app-indsiders-mynotification-api-prod.azurewebsites.net';
  if (isHomolog) return 'https://app-indsiders-mynotification-api-homolog.azurewebsites.net';
  if (origin.includes('localhost')) return 'https://app-indsiders-mynotification-api-dev.azurewebsites.net';
  return 'https://app-indsiders-mynotification-api-dev.azurewebsites.net';
};

export function provideSignalR() {
  const notifications = ref([]);
  const unreadCount = ref(0);
  const isLoadingInitial = ref(false);
  const isLoadingMore = ref(false);
  const hasMore = ref(false);
  const onlyUnread = ref(false);
  
  // State for connection and pagination
  const connection = ref(null);
  const userToken = ref(null);
  const userGuid = ref(null);
  const nextCursor = ref(null);
  const initialLoaded = ref(false);
  const buffer = [];
  const PAGE_SIZE = 20;

  const visibleNotifications = computed(() => {
    if (onlyUnread.value) {
      return notifications.value.filter(n => !n.isRead);
    }
    return notifications.value;
  });

  const recomputeUnread = () => {
    unreadCount.value = notifications.value.filter(n => !n.isRead).length;
  };

  const dedupePush = (items) => {
    const ids = new Set(notifications.value.map(n => n.id));
    const merged = [...notifications.value];
    for (const it of items) {
      if (!ids.has(it.id)) {
        merged.push(it);
        ids.add(it.id);
      }
    }
    // Sort by createdAt desc
    merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    notifications.value = merged;
    recomputeUnread();
  };

  const markAsRead = async (id) => {
    // Optimistic update
    notifications.value = notifications.value.map(n => n.id === id ? { ...n, isRead: true } : n);
    recomputeUnread();

    if (!userToken.value) return;
    try {
      const base = getNotificationApiBaseUrl();
      await fetch(`${base}/api/Notifications/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${userToken.value}`, accept: '*/*' },
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    // Optimistic update
    notifications.value = notifications.value.map(n => ({ ...n, isRead: true }));
    recomputeUnread();

    // Note: Assuming backend might not have a specific "mark all" endpoint yet based on plan, 
    // but if it does or if we iterate, this is the place.
    // For now, the plan.md code showed the UI logic but didn't explicitly show the API call for markAllAsRead 
    // other than the function definition. 
    // If there is an endpoint, it would be POST /api/Notifications/read-all or similar.
    // I will stick to optimistic update as per the plan's provided code snippet which didn't show the fetch.
  };

  const clearAll = () => {
    notifications.value = [];
    recomputeUnread();
  };

  const setOnlyUnread = (val) => {
    onlyUnread.value = val;
  };

  const fetchNotifications = async (cursor = null) => {
    if (!userGuid.value || !userToken.value) return null;
    
    const base = getNotificationApiBaseUrl();
    const url = cursor
      ? `${base}/api/Notifications?UserGuid=${userGuid.value}&Take=${PAGE_SIZE}&Cursor=${encodeURIComponent(cursor)}`
      : `${base}/api/Notifications?UserGuid=${userGuid.value}&Take=${PAGE_SIZE}`;
      
    try {
      const res = await fetch(url, { 
        headers: { Authorization: `Bearer ${userToken.value}`, accept: '*/*' } 
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('Fetch notifications error:', e);
      return null;
    }
  };

  const loadMore = async () => {
    if (isLoadingMore.value || !hasMore.value || !nextCursor.value) return;
    
    isLoadingMore.value = true;
    const data = await fetchNotifications(nextCursor.value);
    
    if (data && Array.isArray(data.data)) {
      const mapped = mapNotifications(data.data);
      dedupePush(mapped);
      nextCursor.value = data.nextCursor;
      hasMore.value = !!data.nextCursor;
    } else {
      hasMore.value = false;
    }
    isLoadingMore.value = false;
  };

  const mapNotifications = (list) => {
    return list.map(n => ({
      id: String(n.id),
      title: n.title || 'Notificação',
      message: n.message || '',
      createdAt: n.createdAt || new Date().toISOString(),
      isRead: Boolean(n.isRead),
      type: n.status || 'info',
      status: String(n.status || ''),
      module: n.module || ''
    }));
  };

  const connect = async (guid, token) => {
    if (!guid || !token) return;
    userGuid.value = guid;
    userToken.value = token;

    // Load Initial
    isLoadingInitial.value = true;
    const data = await fetchNotifications(null);
    
    if (data && Array.isArray(data.data)) {
      const mapped = mapNotifications(data.data);
      dedupePush(mapped);
      nextCursor.value = data.nextCursor;
      hasMore.value = !!data.nextCursor;
    }
    
    isLoadingInitial.value = false;
    initialLoaded.value = true;
    
    if (buffer.length > 0) {
      dedupePush(buffer.splice(0, buffer.length));
    }

    // Start Socket
    const base = getNotificationApiBaseUrl();
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${base}/hubs/notifications?userGuid=${guid}`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    conn.on('ReceiveNotification', (payload) => {
      // Filter out system connection messages
      if (
        payload.title === 'Conectado' || 
        (payload.message && payload.message.includes('Conexão estabelecida'))
      ) {
        return;
      }

      const n = {
        id: String(payload.id || crypto.randomUUID()),
        title: String(payload.title || 'Nova Notificação'),
        message: String(payload.message || ''),
        createdAt: payload.createdAt || new Date().toISOString(),
        isRead: Boolean(payload.isRead),
        type: String(payload.status || 'info'),
        status: String(payload.status || ''),
        module: String(payload.module || ''),
      };

      if (!initialLoaded.value) {
        buffer.unshift(n);
      } else {
        dedupePush([n]);
      }
    });

    try {
      await conn.start();
      connection.value = conn;
    } catch (err) {
      console.error('SignalR Connection Error:', err);
    }
  };

  const disconnect = () => {
    if (connection.value) {
      connection.value.stop();
      connection.value = null;
    }
  };

  onBeforeUnmount(() => {
    disconnect();
  });

  const state = {
    notifications,
    visibleNotifications,
    unreadCount,
    isLoadingInitial,
    isLoadingMore,
    hasMore,
    onlyUnread,
    markAsRead,
    markAllAsRead,
    clearAll,
    setOnlyUnread,
    loadMore,
    connect,
    disconnect
  };

  provide(SignalRKey, state);
  return state;
}

export function useSignalR() {
  const api = inject(SignalRKey);
  if (!api) {
    return { 
      notifications: ref([]), 
      visibleNotifications: ref([]),
      unreadCount: ref(0), 
      isLoadingInitial: ref(false),
      isLoadingMore: ref(false),
      hasMore: ref(false),
      onlyUnread: ref(false),
      markAsRead: async () => {}, 
      markAllAsRead: async () => {},
      clearAll: async () => {},
      setOnlyUnread: () => {},
      loadMore: async () => {},
      connect: () => {},
      disconnect: () => {}
    };
  }
  return api;
}
